import { KrCard } from '@/common/card/KrCard';
import { FilterContainer } from '@/common/dashboard/FilterContainer';
import { Form } from '@/common/form/Form';
import { FormFields } from '@/common/form/rendering/FormFields';
import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { ContentBox } from '@/common/layout/ContentBox';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { far, fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { CalcAIInsights } from './CalcAIInsights';
import { ReallocCalcChart } from './ReallocCalcChart';
import { ReallocCalcTable } from './ReallocCalcTable';

export const CalcDashboard = () => {
  const rsrc = useRsrc();

  const { navState } = useNavState();

  const [fieldDefs, setFieldDefs] = useState();
  const [formValues, setFormValues] = useNavStateSync('formValues');

  const [calcIdToCalcType, setCalcIdToCalcType] = useState();

  useEffect(() => {
    rsrc
      .data('gp', 'CALC_DASHBOARD_FILTERS_DATA_PLUGIN', { periodicity: formValues?.periodicity ?? 'MONTH' })
      .then((res) => {
        const calcIdToCalcType = res.calcIdToCalcType;
        setCalcIdToCalcType(calcIdToCalcType);

        const calcType = calcIdToCalcType[formValues?.calcId ?? res.calcs[0].value];

        setFieldDefs([
          { field: 'calcId', displayName: 'Calculation', type: 'DROPDOWN', values: res.calcs },
          {
            field: 'trxSrcCode',
            displayName: 'Transaction Source Code',
            type: 'DROPDOWN',
            values:
              calcType === 'REALLOC_SPLIT' ?
                [
                  { value: 'DS', displayName: 'Direct Sales' },
                  { value: 'IS', displayName: 'Indirect Sales' },
                  { value: 'RB', displayName: 'Rebates' },
                ]
              : [{ value: 'RB', displayName: 'Rebates' }],
          },
        ]);

        if (!navState.formValues || formValues.calcId !== res.calcs[0].value) {
          setFormValues({
            calcId: formValues?.calcId ?? res.calcs[0].value,
            trxSrcCode: calcType === 'REALLOC_SPLIT' ? 'DS' : 'RB',
          });
        }
      });
  }, [formValues?.calcId]);

  const formatTrxSrcCode = (code) => {
    switch (code) {
      case 'DS':
        return 'Direct Sales';
      case 'IS':
        return 'Indirect Sales';
      case 'RB':
        return 'Rebates';
      default:
        return code;
    }
  };

  if (!fieldDefs) {
    return;
  }

  return (
    <ContentBox>
      {/* Title */}
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Title
          icon={fas.faTasks}
          title={
            calcIdToCalcType[formValues?.calcId] === 'REALLOC_SPLIT' ?
              'Reallocation Split Dashboard'
            : 'Reallocation Month Dashboard'
          }
        />
      </Box>

      {/* Filters */}
      <Form formValues={formValues} setFormValues={setFormValues}>
        <FormFields fieldDefs={fieldDefs} container={FilterContainer} />
      </Form>

      <SpacerLayout>
        <Spacer vertical>
          {/* Row 1 */}
          <Spacer weights={[1, 1]}>
            <KrCard
              title={'Original Lines ' + formatTrxSrcCode(formValues.trxSrcCode) + ' Aggregates'}
              sx={{ flex: 1 }}
            >
              <ReallocCalcChart
                formValues={formValues}
                origLines={true}
                calcType={calcIdToCalcType[formValues?.calcId]}
              />
            </KrCard>
            <KrCard
              title={'Reallocated Lines ' + formatTrxSrcCode(formValues.trxSrcCode) + ' Aggregates'}
              sx={{ flex: 1 }}
            >
              <ReallocCalcChart
                formValues={formValues}
                origLines={false}
                calcType={calcIdToCalcType[formValues?.calcId]}
              />
            </KrCard>
          </Spacer>

          {/* Row 2 */}
          {calcIdToCalcType[formValues?.calcId] === 'REALLOC_SPLIT' && (
            <Spacer weights={[1, 2]}>
              <KrCard
                title="AI Insights"
                icon={far.faSparkles}
                variant="bold"
                sx={{
                  background: 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)',
                  color: '#ecfeff',
                }}
              >
                <CalcAIInsights />
              </KrCard>
              <KrCard title="Aggregates and Ratios">
                <ReallocCalcTable formValues={formValues} />
              </KrCard>
            </Spacer>
          )}
        </Spacer>
      </SpacerLayout>
    </ContentBox>
  );
};
